import LanguageToggle from "@/components/LanguageToggle";
import i18n from "@/i18n";
import { reportError } from "@/utils/reportError";
import { trackSignUp } from "@/utils/analytics";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { flushPendingOnboardingRewards, PENDING_CONTEST_ENTRY_KEY } from "./onboarding-wheel";
import PasswordValidator from "@/components/PasswordValidator";
import SelectionModal from "@/components/SelectionModal";
import ErrorText from "@/components/ui/ErrorText";
import PrimaryButton from "@/components/ui/PrimaryButton";
import { useAuthForm } from "@/hooks/useAuthForm";
import { useCompanies } from "@/hooks/useCompanies";
import { supabase } from "@/utils/supabase";
import { showAlert } from "@/utils/showAlert";
import { getAuthRedirectUrl } from "@/utils/authRedirect";
import { translateSupabaseError } from "@/utils/supabaseErrorTranslations";
import {
    validateEmail,
    validatePassword,
    validateTerms
} from "@/utils/validationUtils";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";

export default function CreateAccountScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [companyModalVisible, setCompanyModalVisible] = useState(false);
  const { companies } = useCompanies();

  const handleRegister = async (formValues: any) => {
    try {
      // First, check if email already exists by attempting to sign in
      // This is needed because Supabase signUp with email confirmation may create "ghost" users
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: formValues.email,
        password: formValues.password,
        options: {
          // Après vérification de l'email, GoTrue redirige vers la page de
          // confirmation du site (« Ton adresse est confirmée », créée par
          // l'agence). L'email est déjà confirmé côté serveur à ce stade ;
          // la page invite à ouvrir l'app. URL présente dans la liste des
          // Redirect URLs autorisées du dashboard Supabase.
          emailRedirectTo: getAuthRedirectUrl("/confirmation"),
          data: {
            company: formValues.company?.trim() || null,
            terms_accepted: formValues.acceptTerms,
            // Langue des emails d'auth (templates GoTrue conditionnels via
            // .Data.language ; absent → français). Synchronisée ensuite à
            // chaque changement de langue (i18n/index.ts → setAppLanguage).
            language: i18n.language?.startsWith("en") ? "en" : "fr",
          },
        },
      });

      // Email déjà enregistré (Supabase renvoie identities = []). Message
      // volontairement NEUTRE : confirmer explicitement qu'un compte existe
      // permettrait d'énumérer les adresses inscrites avec la seule clé anon.
      if (signUpData?.user && signUpData.user.identities?.length === 0) {
        showAlert(
          t("createAccount.emailExistsTitle"),
          t("createAccount.emailExistsMessage"),
          [
            { text: t("login.loginButton"), onPress: () => router.push("/login") },
            { text: t("common.ok"), style: "cancel" },
          ]
        );
        return;
      }

      if (signUpError) {
        const translatedError = translateSupabaseError(signUpError);
        showAlert(translatedError.title, translatedError.message);
        return;
      }

      const data = signUpData;

      if (data.user) {
        trackSignUp();

        // Crédite le bonus de la roue d'onboarding + soumet la participation
        // au tirage capturée avant le signup (logique partagée, idempotente —
        // cf. flushPendingOnboardingRewards dans onboarding-wheel.tsx). Ne
        // s'exécute ici que si une session est créée immédiatement (pas de
        // confirmation email) ; sinon le flush a lieu au premier login via
        // le listener onAuthStateChange de app/_layout.tsx.
        if (data.session) {
          try {
            await flushPendingOnboardingRewards(data.user.id);
          } catch (err) {
            reportError(err);
          }
        }

        // Check if session was created (no email confirmation required)
        if (data.session) {
          // Navigate to notification permission screen
          router.replace("/notification-permission");
        } else {
          // Email confirmation required
          showAlert(
            t("createAccount.confirmEmailTitle"),
            t("createAccount.confirmEmailMessage", { email: formValues.email }),
            [
              {
                text: t("createAccount.understoodButton"),
                onPress: () => router.replace("/login"),
              },
            ]
          );
        }
      }
    } catch (error: any) {
      reportError(error);
      const translatedError = translateSupabaseError(error);
      showAlert(translatedError.title, translatedError.message);
    }
  };

  const form = useAuthForm({
    initialValues: {
      company: "",
      email: "",
      password: "",
      acceptTerms: false,
    },
    validators: {
      // company is optional, no validation required
      email: validateEmail,
      password: validatePassword,
      acceptTerms: validateTerms,
    },
    onSubmit: handleRegister,
  });

  // Préremplit l'email avec celui saisi à la roue d'onboarding (participation
  // au tirage en attente, PENDING_CONTEST_ENTRY_KEY) — l'utilisateur ne le
  // retape pas. Uniquement si le champ est encore vide, pour ne pas écraser
  // une saisie en cours.
  useEffect(() => {
    let cancelled = false;
    AsyncStorage.getItem(PENDING_CONTEST_ENTRY_KEY)
      .then((stored) => {
        if (cancelled || !stored) return;
        const pending = JSON.parse(stored) as { email?: string };
        if (pending?.email && !form.values.email) {
          form.setFieldValue("email", pending.email);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- au montage uniquement
  }, []);

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={styles.container}
    >
      <StatusBar style="dark" />
      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 0.65]}
        style={styles.background}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <LanguageToggle />
          <Text style={styles.title}>{t("login.welcomeTitle")}</Text>

          <View>
            <TouchableOpacity
              style={styles.inputContainer}
              onPress={() => setCompanyModalVisible(true)}
              disabled={form.isSubmitting}
            >
              <Text
                style={[
                  styles.inputText,
                  !!form.values.company && styles.selectedText,
                ]}
              >
                {(form.values.company as string) ||
                  t("createAccount.companyPlaceholder")}
              </Text>
              <Ionicons name="chevron-down" size={20} color="#999" />
            </TouchableOpacity>
          </View>

          <View style={{ marginTop: 16 }}>
            <View
              style={[
                styles.inputContainer,
                form.touched.email && form.errors.email && styles.inputError,
              ]}
            >
              <TextInput
                placeholder={t("login.emailPlaceholder")}
                placeholderTextColor="#999"
                style={styles.input}
                keyboardType="email-address"
                autoCapitalize="none"
                value={form.values.email as string}
                onChangeText={(text) => form.setFieldValue("email", text)}
                onBlur={() => form.handleBlur("email")}
                editable={!form.isSubmitting}
              />
            </View>
            {form.touched.email && <ErrorText error={form.errors.email} />}
          </View>

          <View style={{ marginTop: 16 }}>
            <View
              style={[
                styles.inputContainer,
                form.touched.password &&
                  form.errors.password &&
                  styles.inputError,
              ]}
            >
              <TextInput
                placeholder={t("login.passwordPlaceholder")}
                placeholderTextColor="#999"
                style={[styles.input, { flex: 1 }]}
                secureTextEntry={!showPassword}
                value={form.values.password as string}
                onChangeText={(text) => form.setFieldValue("password", text)}
                onBlur={() => form.handleBlur("password")}
                editable={!form.isSubmitting}
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                <Ionicons
                  name={showPassword ? "eye-outline" : "eye-off-outline"}
                  size={20}
                  color="#999"
                />
              </TouchableOpacity>
            </View>
            <PasswordValidator password={form.values.password as string} />
          </View>

          <View style={{ marginTop: 16 }}>
            <TouchableOpacity
              style={styles.checkboxContainer}
              onPress={() =>
                form.setFieldValue("acceptTerms", !form.values.acceptTerms)
              }
              disabled={form.isSubmitting}
            >
              <View
                style={[
                  styles.checkbox,
                  form.values.acceptTerms && styles.checkboxChecked,
                ]}
              >
                {form.values.acceptTerms && (
                  <Ionicons name="checkmark" size={16} color="#FFF" />
                )}
              </View>
              <Text style={styles.checkboxText}>
                <Trans
                  i18nKey="createAccount.acceptTerms"
                  components={{
                    termsLink: (
                      <Text
                        style={styles.linkText}
                        onPress={(e) => {
                          e.stopPropagation();
                          router.push("/terms-of-use");
                        }}
                      />
                    ),
                    privacyLink: (
                      <Text
                        style={styles.linkText}
                        onPress={(e) => {
                          e.stopPropagation();
                          router.push("/privacy-policy");
                        }}
                      />
                    ),
                  }}
                />
              </Text>
            </TouchableOpacity>
            {form.touched.acceptTerms && (
              <ErrorText error={form.errors.acceptTerms} />
            )}
          </View>

          <View style={{ marginTop: 16 }}>
            <PrimaryButton
              title={
                form.isSubmitting ? (
                  <ActivityIndicator color="#FFF" />
                ) : (
                  t("login.signUpLink")
                )
              }
              onPress={form.handleSubmit}
              disabled={form.isSubmitting}
            />
          </View>

          <View style={styles.loginContainer}>
            <Text style={styles.loginText}>{t("createAccount.alreadyRegistered")}</Text>
            <TouchableOpacity onPress={() => router.push("/login")}>
              <Text style={styles.loginLink}>{t("login.loginButton")}</Text>
            </TouchableOpacity>
          </View>

          <SelectionModal
            visible={companyModalVisible}
            title={t("createAccount.selectCompanyTitle")}
            data={companies.map((c) => c.name)}
            selectedValue={form.values.company as string}
            onSelect={(name) => form.setFieldValue("company", name)}
            onClose={() => setCompanyModalVisible(false)}
            searchable
            searchPlaceholder={t("createAccount.searchCompanyPlaceholder")}
            // Pas de session au moment de l'inscription : impossible d'insérer
            // dans `companies` (policy RLS authenticated). On conserve donc le
            // nom saisi en texte libre (métadonnée company) ; la liaison
            // company_id se fera plus tard depuis l'édition du profil.
            onAddOption={(q) => form.setFieldValue("company", q)}
            addOptionLabel={(q) => t("createAccount.addCompany", { query: q })}
            emptyText={t("createAccount.noCompanyFound")}
          />
        </ScrollView>
      </LinearGradient>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 100,
    paddingBottom: 40,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 28,
    color: "#000",
    marginBottom: 32,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 0,
  },
  halfInputWrapper: {
    width: "48%",
  },
  halfInput: {
    width: "100%",
    marginBottom: 4,
  },
  inputContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    borderWidth: 1,
    borderColor: "transparent",
  },
  inputError: {
    borderColor: "#D32F2F",
  },
  input: {
    flex: 1,
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
    height: "100%",
  },
  inputText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#999",
  },
  selectedText: {
    color: "#000",
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    marginBottom: 4,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#999",
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: {
    backgroundColor: "#3A6B5C",
    borderColor: "#3A6B5C",
  },
  checkboxText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    flex: 1,
  },
  linkText: {
    color: "#8B4513",
    fontFamily: "Inter_700Bold",
  },
  loginContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 24,
  },
  loginText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  loginLink: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#8B4513",
  },
  anonHelpText: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#666",
    marginTop: 6,
    marginLeft: 36,
    lineHeight: 16,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
  },
  modalContent: {
    backgroundColor: "#FFF",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingBottom: 40,
    maxHeight: "80%",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EEE",
  },
  modalTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#000",
  },
  modalItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  modalItemText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#333",
  },
  notificationModalContent: {
    backgroundColor: "#FFF",
    borderRadius: 24,
    padding: 32,
    margin: 24,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  notificationIconContainer: {
    marginBottom: 24,
  },
  notificationTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 24,
    color: "#000",
    marginBottom: 12,
    textAlign: "center",
  },
  notificationDescription: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
    textAlign: "center",
    lineHeight: 24,
    marginBottom: 32,
  },
  notificationButtons: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
  skipNotificationButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: "#F0F0F0",
    alignItems: "center",
  },
  skipNotificationText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "#666",
  },
  enableNotificationButton: {
    flex: 1,
  },
  enableNotificationGradient: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
  },
  enableNotificationText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
});
